import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import {
  deleteInvestigationAssign,
  patchInvestigation,
} from "../../../../../apis";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { OutlineData, OutlineHierarchy } from "../../../../Icon";
import { EGIconButton } from "../../../../MainComponents/Button/IconButton/IconButton";
import { EGButton } from "../../../../MainComponents/Button/EGButton/EGButton";
import { fi } from "date-fns/locale";

export const InvestigationHeader = ({
  id,
  reportDetail,
  investigationList,
  investigation,
  isLoading,
  selectedInvestigation,
  updateSelectedInvestigation,
  reportRender,
  setLoadingInvestigation,
  setShowModalAddInvestigation,
  openConfirmDeleteModal,
  setShowModalHireService,
}) => {
  const addInvestigationValue = "additional_investigation";

  const [t_investigation] = useTranslation("investigation");
  const [isLoadingTitle, setIsLoadingTitle] = useState(false);
  const theme = useTheme();
  // Color base de la investigación, usado por el botón "Contratar servicios"
  const hireServiceColor = investigation?.advance ? "primary" : "success";

  const [invTitle, setInvTitle] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [tryToSaveTitle, setTryToSaveTitle] = useState(false);

  // Función para mostrar texto plano como label
  const getInvLabel = ({ advance, title }) => {
    if (title) return title;

    const tipo = t_investigation(
      advance ? "investigation.advanced" : "investigation.basic",
    );

    const clasificacion = reportDetail?.classification?.label;

    return `${tipo} ${
      clasificacion
        ? `- ${clasificacion}`
        : `- ${t_investigation("Sin clasificación")}`
    }`;
  };

  // Lista de investigaciones parseada
  const paresedInvestigations = useMemo(() => {
    return [
      ...investigationList.map((item) => ({
        ...item,
        title: getInvLabel(item),
      })),
      {
        title: `+ ${t_investigation("header.add_investigation")}`,
        alias: addInvestigationValue,
      },
    ];
  }, [investigationList, reportDetail]);

  // Sync cuando cambia la investigación seleccionada
  useEffect(() => {
    setInvTitle(
      investigationList.find((item) => item.alias === selectedInvestigation)
        ?.title || "",
    );
    setIsEditing(false);
    setTryToSaveTitle(false);
  }, [selectedInvestigation]);

  // Guardar nombre editado
  const hdlSaveTitle = async () => {
    setTryToSaveTitle(true);
    if (!(invTitle.length > 2)) return;
    const saving = enqueueSnackbar(`${t_investigation("General.loading")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      setIsLoadingTitle(true);
      await patchInvestigation(selectedInvestigation, {
        folder: { title: invTitle },
      });
      reportRender();
      setIsEditing(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingTitle(false);
      closeSnackbar(saving);
    }
  };

  // Cambiar selección
  const handleChangeSelectedInvestigation = (e) => {
    if (e.target.value === addInvestigationValue) {
      setShowModalAddInvestigation(true);
    } else {
      updateSelectedInvestigation(e.target.value, true);
    }
  };

  // Desvincular
  const handleUnlinkInvestigation = async () => {
    setLoadingInvestigation(true);
    const load = enqueueSnackbar(`${t_investigation("General.loading")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      await deleteInvestigationAssign(selectedInvestigation, id);
      closeSnackbar(load);
      enqueueSnackbar(
        t_investigation("investigation.investigation_unlinked_success"),
        { variant: "success" },
      );
      setLoadingInvestigation(false);
      reportRender();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        t_investigation("error_unlinking_investigation_from_report"),
      );
      setLoadingInvestigation(false);
    }
  };

  const confirmUnlinkInvestigation = () => {
    openConfirmDeleteModal({ onConfirm: handleUnlinkInvestigation });
  };

  return (
    <Grid2 mt={2} container spacing={2}>
      {/* Panel izquierdo */}
      <Grid2
        size={{ xs: 12, lg: 8 }}
        className="dyTheme1 dyBorder1 rounded"
        gridTemplateColumns="5px auto"
        overflow="hidden"
        p={0}
        display="grid"
      >
        <Box
          sx={{
            backgroundColor: investigation.advance
              ? theme.palette.ethicsBlue.main
              : theme.palette.green.main,
          }}
        />

        <Box
          p={2}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "flex-start",
            gap: 1.5,
          }}
        >
          {/* Icono + título + botones edit/delete */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              flex: { xs: "0 1 auto", md: "1 1 70%" },
              minWidth: 0,
            }}
          >
            {/* Icon */}
            <Box
              height={24}
              width={24}
              borderRadius={0.8}
              backgroundColor={
                investigation.advance
                  ? theme.palette.primary.main
                  : theme.palette.success.main
              }
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              {investigation.advance ? (
                <OutlineData color={theme.palette.white.main} size={16} />
              ) : (
                <OutlineHierarchy
                  color={theme.palette.common.white}
                  size={16}
                />
              )}
            </Box>

            {/* Investigation title */}
            {isEditing ? (
              <EGInput
                value={invTitle}
                label={`${t_investigation("header.investigation_name")}:`}
                onChange={(e) => setInvTitle(e.target.value)}
                variant="outlined"
                isValid={tryToSaveTitle ? invTitle.length > 2 : null}
                showSuccessValidation
                showHelpText={false}
                sx={{ width: "100%" }}
                disabled={isLoadingTitle}
              />
            ) : (
              <Typography
                component="span"
                fontWeight={600}
                variant="h5"
                sx={{ wordBreak: "break-word" }}
              >
                {invTitle || getInvLabel(investigation)}
              </Typography>
            )}

            {/* Botones edit/delete o cancel/save */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={isEditing ? 1 : 0}
              flexShrink={0}
            >
              {!isEditing ? (
                <>
                  <EGIconButton
                    iconName="outline_edit"
                    iconSize={20}
                    iconColor="info"
                    iconColorHover="white"
                    colorHover="info"
                    onClick={() => {
                      const currentTitle =
                        investigationList.find(
                          (item) => item.alias === selectedInvestigation,
                        )?.title || "";
                      setInvTitle(currentTitle);
                      setIsEditing(true);
                      setTryToSaveTitle(false);
                    }}
                    title={t_investigation("investigation.edit_title")}
                    disabled={isLoadingTitle}
                  />
                  <EGIconButton
                    iconName="trash_can"
                    iconSize={20}
                    iconColor="error"
                    iconColorHover="white"
                    colorHover="error"
                    onClick={() =>
                      openConfirmDeleteModal({
                        onConfirm: () => handleUnlinkInvestigation(),
                      })
                    }
                    title={t_investigation(
                      "investigation.delete_investigation",
                    )}
                    disabled={isLoadingTitle}
                  />
                </>
              ) : (
                <>
                  <EGIconButton
                    iconName="close_icon"
                    iconColor="white"
                    color="error"
                    title={t_investigation("investigation.cancel")}
                    placement="top"
                    onClick={() => {
                      setIsEditing(false);
                      setInvTitle(
                        investigationList.find(
                          (item) => item.alias === selectedInvestigation,
                        )?.title || "",
                      );
                    }}
                    sx={{ height: 24, width: 24, padding: 0 }}
                  />
                  <EGIconButton
                    iconName="check_icon"
                    iconColor="white"
                    color="success"
                    title={t_investigation("investigation.save")}
                    placement="top"
                    onClick={hdlSaveTitle}
                    sx={{ height: 24, width: 24, padding: 0 }}
                  />
                </>
              )}
            </Stack>
          </Stack>

          {/* Botón "Contratar servicio": al lado en md+, abajo en xs/sm */}
          {!isEditing && setShowModalHireService && (
            <Box
              sx={{
                flex: { xs: "0 0 auto", md: "1 1 30%" },
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                minWidth: 0,
              }}
            >
              <EGButton
                label={t_investigation("sections.hire_service")}
                color={hireServiceColor}
                iconName="outline_search_eg"
                iconSize={18}
                labelColor="white"
                iconColor="white"
                iconColorHover="white"
                labelColorHover="white"
                onClick={() => setShowModalHireService(true)}
                disabled
                sx={{ width: "100%", maxWidth: "350px" }}
              />
            </Box>
          )}
        </Box>
      </Grid2>

      {/* Panel derecho - selector */}
      <Grid2
        className="dyTheme1 dyBorder1 rounded"
        display="flex"
        alignItems="center"
        size={{ xs: 12, lg: 4 }}
        py={1.3}
        px={1.5}
      >
        <EGBasicSelect
          options={paresedInvestigations}
          optionLabel="title"
          optionValue="alias"
          label={t_investigation("header.select_investigation")}
          value={selectedInvestigation}
          onChange={handleChangeSelectedInvestigation}
          disabled={isLoading}
        />
      </Grid2>
    </Grid2>
  );
};
